/*
 * cf-current-section-parts.js — Click Fencing — 2026-09-07
 *
 * Pete: "when going through the calculator, in the parts order list on the right hand
 * side of the calculator it shows you all sections parts. for all calculators i want
 * just the section its on to show those parts. not the other sections."
 *
 * WHAT IT DOES
 * ------------
 * On the CONFIGURE step only, the quote panel shows the parts for the section the
 * customer is actually working on. The other sections collapse to a single muted line
 * carrying their combined subtotal, so the panel still adds up to the Total underneath
 * it. Review & Checkout is untouched — that is where the whole job belongs.
 *
 * WHY THE OTHER SECTIONS ARE SUMMARISED RATHER THAN DELETED
 * ---------------------------------------------------------
 * The Total is the whole job. Hiding $226.81 of parts while still printing a Total that
 * includes them makes the panel look like it cannot add up, which is the exact fault the
 * vault already records: if a breakdown does not sum to its own total, do not print the
 * breakdown. One line — "Other sections (1) $226.81" — keeps the sum honest and still
 * answers Pete's complaint, because no other section's PARTS are listed.
 *
 * TWO DIFFERENT PANELS, BECAUSE THE CALCULATORS DISAGREE
 * ------------------------------------------------------
 * Flat top, blade and perfmesh render `#breakdown-content-configure` as one
 * `.breakdown-section` per section, each headed "Section N". Filtering those is pure
 * hiding — no arithmetic, nothing to get wrong.
 *
 * GLASS DOES NOT GROUP BY SECTION AT ALL. `#breakdown-content` groups by PRODUCT
 * CATEGORY ("Frameless Glass", "Hardware", "Pickup") and MERGES quantities across
 * sections, so there is nothing to hide — a single line can carry panels from two
 * different sections. Glass therefore has to be rebuilt from
 * `_calcSectionQuote(sectionNum, section, calculation)`, which returns
 * `{subtotal, breakdown:[{category,item,quantity,unitPrice,totalPrice}]}`.
 * Verified on 2026-09-07 against a live two-section job: 774.18 + 443.34 = 1217.52,
 * exactly the rendered Total.
 *
 * THE RECONCILIATION GUARD — the important part
 * ---------------------------------------------
 * Before glass is rebuilt, every section is priced and the sum is compared against the
 * subtotals the calculator itself printed. More than a cent apart and the patch RESTORES
 * the panel and does nothing. Showing the customer a number we computed ourselves and got
 * wrong is far worse than showing them one section too many.
 *
 * NOTHING IS DESTROYED
 * --------------------
 * Original nodes are hidden, never removed, and anything this file creates carries
 * `.cf-cs-gen`. `restore()` puts the panel back exactly as the calculator rendered it and
 * runs first on every pass, so the transform is idempotent and a thrown error can always
 * unwind. That is also why re-entrancy is handled with a flag cleared in `finally`
 * within one synchronous call — the skill records a `__cfSnapping` guard that stuck true
 * and silently disabled every later correction.
 */
(function () {
  "use strict";

  var PAGES = /(blade-fence-calculator|flat-top-fence-calculator|perfmesh-fence|glass-fencing)/;
  if (!PAGES.test(location.pathname)) return;
  if (window.__cfCurrentSectionParts) return;
  window.__cfCurrentSectionParts = true;

  var GEN = "cf-cs-gen";          // anything this file created
  var HID = "data-cf-cs-hidden";  // anything this file hid
  var busy = false;
  var TRIES = 0, MAX_TRIES = 200;

  function inst() { return window.glassCalculator || window.glassFencingCalculator || null; }

  function money(n) {
    return "$" + (Math.round(Number(n) * 100) / 100).toFixed(2);
  }

  // "$1,217.52" -> 1217.52. Returns null when there is no number to read, which is a
  // signal to bail rather than a licence to guess.
  function readMoney(text) {
    if (!text) return null;
    var m = String(text).match(/\$\s*([\d,]+(?:\.\d{1,2})?)/);
    if (!m) return null;
    var v = parseFloat(m[1].replace(/,/g, ""));
    return isFinite(v) ? v : null;
  }

  // The panel the customer is looking at right now. Review & Checkout renders into
  // `-final` containers and must never be touched.
  function livePanel() {
    var all = [].slice.call(document.querySelectorAll(
      "#breakdown-content-configure, #breakdown-content"
    ));
    // Duplicate ids exist in this theme (two #breakdown-content-configure on flat top
    // and perfmesh), so pick the one actually on screen.
    for (var i = 0; i < all.length; i++) {
      if (all[i].offsetParent !== null) return all[i];
    }
    return null;
  }

  function onReviewStep() {
    var f = document.getElementById("breakdown-content-final");
    return !!(f && f.offsetParent !== null);
  }

  function restore(host) {
    if (!host) return;
    var gen = host.querySelectorAll("." + GEN);
    for (var i = 0; i < gen.length; i++) {
      if (gen[i].parentNode) gen[i].parentNode.removeChild(gen[i]);
    }
    var hid = host.querySelectorAll("[" + HID + "]");
    for (var j = 0; j < hid.length; j++) {
      hid[j].style.display = hid[j].getAttribute(HID) === "-" ? "" : hid[j].getAttribute(HID);
      hid[j].removeAttribute(HID);
    }
  }

  function hide(el) {
    if (!el || el.hasAttribute(HID)) return;
    el.setAttribute(HID, el.style.display || "-");
    el.style.display = "none";
  }

  // Built by cloning a real header from the panel so it inherits whatever inline styling
  // that calculator uses — the four of them do not share a stylesheet for this.
  function summaryRow(templateGroup, count, subtotal) {
    var row = templateGroup.cloneNode(true);
    row.className = (templateGroup.className || "") + " " + GEN;
    row.removeAttribute(HID);
    row.style.display = "";

    // Keep only the header; drop the line items.
    var kids = [].slice.call(row.children);
    for (var i = 1; i < kids.length; i++) row.removeChild(kids[i]);

    var head = row.firstElementChild;
    if (!head) return null;
    var h = head.querySelector("h5");
    var spans = head.querySelectorAll("span");
    var amount = spans.length ? spans[spans.length - 1] : null;

    if (h) {
      h.textContent = count === 1 ? "Other section (1)" : "Other sections (" + count + ")";
      h.style.color = "#94a3b8";
      h.style.fontStyle = "italic";
    }
    if (amount) {
      amount.textContent = subtotal == null ? "" : money(subtotal);
      amount.style.color = "#94a3b8";
    }

    var note = document.createElement("div");
    note.style.cssText = "font-size:0.78rem;color:#94a3b8;padding:2px 0 6px;line-height:1.45;";
    note.textContent = "Listed in full on the next step. The Total below covers the whole job.";
    row.appendChild(note);

    return row;
  }

  /* ---------------------------------------------------------------------------
   * Flat top / blade / perfmesh — one `.breakdown-section` per section already.
   * Pure hiding: no number is recomputed, so there is nothing to reconcile.
   * ------------------------------------------------------------------------- */
  function applyGrouped(host, cur) {
    var groups = [].slice.call(host.querySelectorAll(":scope > .breakdown-section"));
    if (!groups.length) return false;

    var others = [], mine = null, othersTotal = 0, unreadable = false;

    for (var i = 0; i < groups.length; i++) {
      var h5 = groups[i].querySelector("h5");
      var label = h5 ? h5.textContent.trim() : "";
      var m = label.match(/^Section\s+(\d+)\b/i);
      if (!m) continue;                       // Pickup / Delivery — job level, leave it
      if (parseInt(m[1], 10) === cur) { mine = groups[i]; continue; }
      others.push(groups[i]);
      var sub = readMoney(groups[i].firstElementChild ? groups[i].firstElementChild.textContent : "");
      if (sub == null) unreadable = true; else othersTotal += sub;
    }

    if (!others.length) return false;

    var tpl = mine || others[0];
    var row = summaryRow(tpl, others.length, unreadable ? null : othersTotal);
    if (row) others[others.length - 1].parentNode.insertBefore(row, others[others.length - 1].nextSibling);
    for (var k = 0; k < others.length; k++) hide(others[k]);
    return true;
  }

  /* ---------------------------------------------------------------------------
   * Glass — groups by product category and merges across sections, so the current
   * section's lines have to be priced rather than filtered.
   * ------------------------------------------------------------------------- */
  function applyGlass(host, fc, cur, count) {
    if (typeof fc._calcSectionQuote !== "function") return false;

    // ONLY sections the customer has actually filled in. A section with no length is
    // not in the rendered panel either, so counting it would make our arithmetic
    // disagree with the calculator's and trip the guard below for no reason — and it
    // would put a meaningless "Other section (1) $0.00" line on screen.
    var active = [], i, n;
    for (n = 1; n <= count; n++) {
      var s = fc.state.sections[n];
      if (s && s.length && fc.state.optimizedConfigs[n]) active.push(n);
    }
    if (active.indexOf(cur) === -1) return false;   // nothing priced for this section yet
    if (active.length < 2) return false;            // nothing of anyone else's to hide

    var quotes = {};
    for (i = 0; i < active.length; i++) {
      n = active[i];
      try {
        quotes[n] = fc._calcSectionQuote(n, fc.state.sections[n], fc.state.optimizedConfigs[n]);
      } catch (e) { return false; }
      if (!quotes[n] || !Array.isArray(quotes[n].breakdown)) return false;
    }

    // Which rendered categories are section-attributable? Anything the per-section
    // calc produces. Everything else (Pickup, Delivery) is job level and stays put.
    var owned = {};
    for (i = 0; i < active.length; i++) {
      n = active[i];
      for (var b2 = 0; b2 < quotes[n].breakdown.length; b2++) {
        owned[String(quotes[n].breakdown[b2].category || "").trim()] = true;
      }
    }

    var cats = [].slice.call(host.querySelectorAll(":scope > .breakdown-category"));
    var target = [], renderedOwned = 0, readFailed = false;
    for (i = 0; i < cats.length; i++) {
      var h5 = cats[i].querySelector("h5");
      var name = h5 ? h5.textContent.trim() : "";
      if (!owned[name]) continue;
      target.push(cats[i]);
      var sub = readMoney(cats[i].firstElementChild ? cats[i].firstElementChild.textContent : "");
      if (sub == null) readFailed = true; else renderedOwned += sub;
    }
    if (!target.length) return false;

    // THE GUARD. Our arithmetic must agree with the calculator's own printed subtotals
    // to the cent, or we do not touch the panel.
    var computedAll = 0;
    for (i = 0; i < active.length; i++) computedAll += Number(quotes[active[i]].subtotal) || 0;
    if (readFailed || Math.abs(computedAll - renderedOwned) > 0.011) {
      try {
        console.warn("[cf-current-section-parts] glass reconciliation failed — panel left alone.",
          "computed", computedAll, "rendered", renderedOwned);
      } catch (e2) {}
      return false;
    }

    var mineQ = quotes[cur];
    if (!mineQ) return false;

    // Rebuild the current section's lines, grouped by category, cloning the real markup
    // so the styling matches whatever the theme is doing today.
    var byCat = {}, order = [];
    for (i = 0; i < mineQ.breakdown.length; i++) {
      var b = mineQ.breakdown[i];
      var c = String(b.category || "").trim();
      if (!byCat[c]) { byCat[c] = []; order.push(c); }
      byCat[c].push(b);
    }

    var itemTpl = target[0].querySelector(".breakdown-item");
    if (!itemTpl) return false;
    var anchor = target[0];

    for (i = 0; i < order.length; i++) {
      var catName = order[i];
      var source = null;
      for (var t = 0; t < target.length; t++) {
        var th = target[t].querySelector("h5");
        if (th && th.textContent.trim() === catName) { source = target[t]; break; }
      }
      if (!source) source = target[0];

      var group = source.cloneNode(false);
      group.className = (source.className || "") + " " + GEN;
      group.style.display = "";

      var head = source.firstElementChild.cloneNode(true);
      var lines = byCat[catName], catTotal = 0;
      for (var L = 0; L < lines.length; L++) catTotal += Number(lines[L].totalPrice) || 0;
      var hh = head.querySelector("h5");
      if (hh) hh.textContent = catName;
      var hspans = head.querySelectorAll("span");
      if (hspans.length) hspans[hspans.length - 1].textContent = money(catTotal);
      group.appendChild(head);

      // Take the row template from THIS category, not from the first one. The unit
      // wording differs per category — glass panels read "/ panel", spigots read
      // "each" — and cloning across categories relabelled every spigot "/ panel".
      var rowTpl = source.querySelector(".breakdown-item") || itemTpl;

      for (L = 0; L < lines.length; L++) {
        var row = rowTpl.cloneNode(true);
        var nameEl = row.querySelector(".item-name");
        var qtyEl = row.querySelector(".item-quantity");
        var priceDivs = row.querySelectorAll(".item-pricing > div");
        if (nameEl) nameEl.textContent = String(lines[L].item || "");
        if (qtyEl) qtyEl.textContent = "× " + (lines[L].quantity != null ? lines[L].quantity : "");
        if (priceDivs.length >= 2) {
          // Keep whatever unit wording the theme used ("/ panel", "each").
          var unitWas = priceDivs[0].textContent || "";
          var suffix = unitWas.replace(/^\s*\$[\d,.]+\s*/, "").trim();
          priceDivs[0].textContent = money(lines[L].unitPrice) + (suffix ? " " + suffix : "");
          priceDivs[1].textContent = money(lines[L].totalPrice);
        }
        group.appendChild(row);
      }
      anchor.parentNode.insertBefore(group, anchor);
    }

    var othersTotal = computedAll - (Number(mineQ.subtotal) || 0);
    var summary = summaryRow(target[0], active.length - 1, othersTotal);
    if (summary) anchor.parentNode.insertBefore(summary, anchor);

    for (i = 0; i < target.length; i++) hide(target[i]);
    return true;
  }

  function apply() {
    if (busy) return;
    busy = true;
    var host = null;
    try {
      var fc = inst();
      if (!fc || !fc.state) return;
      host = livePanel();
      if (!host) return;

      restore(host);                       // always start from what the calculator drew

      if (onReviewStep()) return;          // whole job belongs on Review & Checkout
      var count = parseInt(fc.state.sectionsCount, 10) || 0;
      var cur = parseInt(fc.state.currentSection, 10) || 1;
      if (count < 2) return;               // nothing to hide
      if (cur < 1 || cur > count) return;

      if (host.querySelector(":scope > .breakdown-section")) applyGrouped(host, cur);
      else applyGlass(host, fc, cur, count);
    } catch (e) {
      try { restore(host); } catch (e2) {}
      try { console.warn("[cf-current-section-parts] left the panel alone:", e && e.message); } catch (e3) {}
    } finally {
      busy = false;                        // synchronous: cannot deadlock
    }
  }

  function after(P, name) {
    if (typeof P[name] !== "function") return;
    var orig = P[name];
    P[name] = function () {
      var r = orig.apply(this, arguments);
      try { apply(); } catch (e) {}
      return r;
    };
  }

  function install() {
    var fc = inst();
    if (!fc || !fc.state) return false;
    var P = Object.getPrototypeOf(fc);
    if (!P || P.__cfCurrentSectionPartsInstalled) return true;

    // Every path that repaints the panel or changes which section is in focus.
    after(P, "updateQuoteDisplay");
    after(P, "updateQuoteBreakdown");
    after(P, "updateComponentsQuote");
    after(P, "showSection");
    after(P, "updateCalculationResults");

    P.__cfCurrentSectionPartsInstalled = true;
    apply();
    try { console.log("[cf-current-section-parts] installed on " + location.pathname); } catch (e) {}
    return true;
  }

  function tryInstall() {
    var done = false;
    try { done = install(); } catch (e) {}
    if (!done && ++TRIES < MAX_TRIES) setTimeout(tryInstall, 100);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", tryInstall);
  } else {
    tryInstall();
  }
})();
